import { BadgeStatus, DateBagde, IconBadge } from "src/app/shared/interfaces/badges.interface";
import { COLORS_BADGE } from "src/app/shared/enums/global-constants";
import { localDB } from "src/app/shared/helpers/localStorage";
import { AAllTareoStatus, DataErrorResult, PaginateState, StatusTask, TempTaskModal, UpdateValidation } from "../interfaces/task.interface";
import moment from "moment";
import { configurations } from "src/app/shared/interfaces/configurations.interface";
import { groupBy } from "src/app/shared/helpers/groupBy";
import { GenericResponse } from "src/app/shared/interfaces/generic.interface";
import { getSimpleBadge, getStateBadge } from "src/app/shared/helpers/badgesBuilder";
import { setTotalHours } from "src/app/shared/helpers/timeAndHours";
import { ASSISTANCE_STATUS } from "../../core/attendance/helpers/enums";
import { SmartAlertService } from "src/app/shared/components/smart-alert/smart-alert.service";
import { ComponentInitConfigTask } from "../pages/configs/initListTable.config";
import { getEmptyIcon } from "./mapData";
import { MarkDetail } from "src/app/shared/components/maps/interface/interface";
import { CollaboratorMarksResponse } from "src/app/core/layout/components/interval-marks/interfaces/interfaces";
import { ACTIVITY_REVISION_STATUS, ACTIVITY_STATUS, TIME_TYPE } from "src/app/shared/enums/task.enum";

export const toOrder = (input: string) => {
  if (input) {
    const uniqueNumbers = new Set(input.split('-').map(Number));
    const result = Array.from(uniqueNumbers).sort((a, b) => b - a);
    return result.join('-');
  }
  return
};



const currentJornadaDay = () => {
  const now = moment();
  return now.hour() <= 6
    ? now.clone().subtract(1, 'day').startOf('day')
    : now.clone().startOf('day');
};

const overlapMinutes = (
  aStart: moment.Moment,
  aEnd: moment.Moment,
  bStart: moment.Moment,
  bEnd: moment.Moment
) => {
  const start = moment.max(aStart, bStart);
  const end = moment.min(aEnd, bEnd);
  if (!end.isAfter(start)) return 0;
  return end.diff(start, 'minutes');
};

const realWorkIntervals = (
  marcas: MarkDetail[]
): { start: moment.Moment; end: moment.Moment }[] => {
  if (!marcas?.length) return [];
  const jornada = currentJornadaDay();
  const now = moment();
  const groups = groupBy(marcas, mark => mark.nInterval);
  const intervals: { start?: moment.Moment; end?: moment.Moment; open: boolean }[] = [];

  Object.values(groups).forEach((marks) => {
    const sorted = [...(marks as MarkDetail[])].sort((a, b) => moment(a.dDate).diff(moment(b.dDate)));
    const hasTypes = sorted.some(mark => mark.nType_Mark === 1 || mark.nType_Mark === 2);
    let start: moment.Moment | undefined;
    let end: moment.Moment | undefined;

    if (hasTypes) {
      const entry = sorted.find(mark => mark.nType_Mark === 1);
      const exit = sorted.find(mark => mark.nType_Mark === 2);
      if (entry) start = moment(entry.dDate);
      if (exit) end = moment(exit.dDate);
    } else if (sorted.length >= 2) {
      start = moment(sorted[0].dDate);
      end = moment(sorted[sorted.length - 1].dDate);
    } else if (sorted.length === 1) {
      start = moment(sorted[0].dDate);
    }

    intervals.push({ start, end, open: !end });
  });

  const openToday = intervals
    .filter(interval => interval.open && interval.start && interval.start.isSame(jornada, 'day') && interval.start.isBefore(now))
    .sort((a, b) => b.start!.diff(a.start))[0];
  if (openToday?.start) {
    openToday.end = now.clone();
  }

  return intervals
    .filter(interval => !!interval.start && !!interval.end && interval.end.isAfter(interval.start))
    .map(interval => ({ start: interval.start as moment.Moment, end: interval.end as moment.Moment }))
    .sort((a, b) => a.start.diff(b.start));
};

const scheduleBlocks = (horarios: CollaboratorMarksResponse[]) => {
  const groups = groupBy((horarios ?? []).filter(item => item.dtime), item => item.nInterval);
  const blocks: { start: moment.Moment; end: moment.Moment; tolerance: number }[] = [];

  Object.values(groups).forEach((marks) => {
    const sorted = [...(marks as CollaboratorMarksResponse[])].sort((a, b) => moment(a.dtime).diff(moment(b.dtime)));
    for (let i = 0; i + 1 < sorted.length; i += 2) {
      const start = moment(sorted[i].dtime);
      const end = moment(sorted[i + 1].dtime);
      if (end.isAfter(start)) {
        blocks.push({ start, end, tolerance: sorted[i].nTolerancia ?? 0 });
      }
    }
  });

  return blocks.sort((a, b) => a.start.diff(b.start));
};

export const getMinutesInSchedule = (
  marcas: MarkDetail[],
  horarios: CollaboratorMarksResponse[]
): { dentro: number; fuera: number, endHour: string } => {
  const intervals = realWorkIntervals(marcas ?? []);
  const blocks = scheduleBlocks(horarios ?? []);
  if (!blocks.length) {
    const fuera = intervals.reduce((sum, interval) => sum + interval.end.diff(interval.start, 'minutes'), 0);
    return { dentro: 0, fuera, endHour: '00:00:00' };
  }

  const first = blocks[0];
  const last = blocks[blocks.length - 1];
  const tardiness = intervals[0] ? intervals[0].start.diff(first.start, 'minutes') : 0;
  const minsTardiness = tardiness > 0 && tardiness <= first.tolerance ? tardiness : 0;
  const adjustedLastEnd = last.end.clone().add(minsTardiness, 'minutes');
  const workBlocks = blocks.map((block, index) => ({
    start: block.start,
    end: index === blocks.length - 1 ? adjustedLastEnd : block.end,
  }));

  let dentro = 0;
  let fuera = 0;
  intervals.forEach(interval => {
    workBlocks.forEach(block => {
      dentro += overlapMinutes(interval.start, interval.end, block.start, block.end);
    });
    if (interval.start.isBefore(first.start)) {
      fuera += overlapMinutes(interval.start, interval.end, interval.start, first.start);
    }
    if (interval.end.isAfter(adjustedLastEnd)) {
      fuera += overlapMinutes(interval.start, interval.end, adjustedLastEnd, interval.end);
    }
  });

  return { dentro, fuera, endHour: adjustedLastEnd.format('HH:mm:ss') };
};

export interface OutOfScheduleRange {
  start: moment.Moment;
  end: moment.Moment;
}

export const marksOfJornada = (marcas: MarkDetail[], dDate: string): MarkDetail[] => {
  const source = marcas ?? [];
  const sameDay = source.filter(mark => moment(mark.dDate).isSame(dDate, 'day'));
  const started = new Set(
    sameDay.filter(mark => mark.nType_Mark === 1).map(mark => mark.nInterval)
  );
  if (!started.size) return sameDay;
  return source.filter(mark => started.has(mark.nInterval));
};

const buildIntervalsFromMarcas = (
  sortedMarcas: MarkDetail[]
): { start: moment.Moment; end: moment.Moment }[] => realWorkIntervals(sortedMarcas);

export const getOutOfScheduleRanges = (
  marcas: MarkDetail[],
  horarios: CollaboratorMarksResponse[]
): OutOfScheduleRange[] => {
  if (!marcas?.length) return [];
  const intervals = realWorkIntervals(marcas);
  const blocks = scheduleBlocks(horarios ?? []);
  if (!blocks.length) {
    return intervals;
  }

  const first = blocks[0];
  const last = blocks[blocks.length - 1];
  const tardiness = intervals[0] ? intervals[0].start.diff(first.start, 'minutes') : 0;
  const minsTardiness = tardiness > 0 && tardiness <= first.tolerance ? tardiness : 0;
  const adjustedLastEnd = last.end.clone().add(minsTardiness, 'minutes');
  const ranges: OutOfScheduleRange[] = [];

  intervals.forEach(interval => {
    if (interval.start.isBefore(first.start)) {
      const end = moment.min(interval.end, first.start);
      if (end.isAfter(interval.start)) {
        ranges.push({ start: interval.start.clone(), end: end.clone() });
      }
    }
    if (interval.end.isAfter(adjustedLastEnd)) {
      const start = moment.max(interval.start, adjustedLastEnd);
      if (interval.end.isAfter(start)) {
        ranges.push({ start: start.clone(), end: interval.end.clone() });
      }
    }
  });

  return ranges.sort((a, b) => a.start.diff(b.start));
};

export const getOutOfScheduleStartHour = (
  marcas: MarkDetail[],
  horarios: CollaboratorMarksResponse[],
  fallbackHour: string
): string => {
  const ranges = getOutOfScheduleRanges(marcas, horarios);
  if (!ranges.length) return fallbackHour;
  return ranges[0].start.format('HH:mm:ss');
};

export const isWithinOutOfScheduleRange = (
  marcas: MarkDetail[],
  horarios: CollaboratorMarksResponse[],
  rangeStart: moment.Moment,
  rangeEnd: moment.Moment
): boolean => {
  const ranges = getOutOfScheduleRanges(marcas, horarios);
  return isInsideOutOfScheduleRanges(ranges, rangeStart, rangeEnd);
};

export const isInsideOutOfScheduleRanges = (
  ranges: OutOfScheduleRange[],
  rangeStart: moment.Moment,
  rangeEnd: moment.Moment
): boolean => {
  if (!ranges.length) return false;
  return ranges.some(r =>
    rangeStart.isSameOrAfter(r.start) && rangeEnd.isSameOrBefore(r.end)
  );
};

const punchOccupiedRange = (
  range: OutOfScheduleRange,
  occupied: OutOfScheduleRange
): OutOfScheduleRange[] => {
  if (occupied.end.isSameOrBefore(range.start) || occupied.start.isSameOrAfter(range.end)) {
    return [range];
  }
  if (occupied.start.isSameOrBefore(range.start) && occupied.end.isSameOrAfter(range.end)) {
    return [];
  }
  if (occupied.start.isSameOrBefore(range.start) && occupied.end.isBefore(range.end)) {
    return [{ start: occupied.end.clone(), end: range.end.clone() }];
  }
  if (occupied.start.isAfter(range.start) && occupied.end.isSameOrAfter(range.end)) {
    return [{ start: range.start.clone(), end: occupied.start.clone() }];
  }
  return [
    { start: range.start.clone(), end: occupied.start.clone() },
    { start: occupied.end.clone(), end: range.end.clone() },
  ];
};

export const subtractOccupiedRanges = (
  allowed: OutOfScheduleRange[],
  occupied: OutOfScheduleRange[]
): OutOfScheduleRange[] => {
  let remaining = allowed.map(range => ({
    start: range.start.clone(),
    end: range.end.clone(),
  }));

  for (const block of occupied) {
    const next: OutOfScheduleRange[] = [];
    for (const range of remaining) {
      next.push(...punchOccupiedRange(range, block));
    }
    remaining = next;
  }

  return remaining.filter(range => range.end.diff(range.start, 'minutes') > 0);
};

export const withoutNextDawnSlices = (
  ranges: OutOfScheduleRange[],
  jornadaDate: string
): { kept: OutOfScheduleRange[]; droppedMinutes: number } => {
  const nextDay = moment(jornadaDate).startOf('day').add(1, 'day');
  const kept: OutOfScheduleRange[] = [];
  let droppedMinutes = 0;
  for (const range of ranges) {
    if (!range.start.clone().startOf('day').isBefore(nextDay)) {
      droppedMinutes += range.end.diff(range.start, 'minutes');
      continue;
    }
    kept.push(range);
  }
  return { kept, droppedMinutes };
};

export const formatOutOfScheduleRangesMessage = (
  ranges: OutOfScheduleRange[],
  fullyBooked = false,
  referenceDate?: string
): string => {
  const visible = ranges
    .map((range) => ({
      range,
      minutes: range.end.diff(range.start, 'minutes'),
    }))
    .filter((item) => item.minutes > 0)
    .sort((a, b) => a.range.start.diff(b.range.start));

  if (!visible.length) {
    if (fullyBooked) {
      return `<p>Ya no tienes rangos disponibles para registrar tareo no regular en este día.</p>`;
    }
    return `<p><strong>No estás dentro del rango de marcación.</strong></p>`;
  }

  const intro = visible.length === 1
    ? 'Solo tienes permitido registrar tareo no regular en el siguiente rango:'
    : 'Solo tienes permitido registrar tareo no regular en los siguientes rangos:';

  const lines = visible
    .map(({ range, minutes }) => {
      const reference = referenceDate ? moment(referenceDate).startOf('day') : null;
      const startsNextDay = !!reference && range.start.clone().startOf('day').isAfter(reference);
      const endsNextDay = range.end.clone().startOf('day').isAfter(range.start.clone().startOf('day'));
      const endLabel = endsNextDay || startsNextDay
        ? `${range.end.format('HH:mm')} del día siguiente`
        : range.end.format('HH:mm');
      return `- <strong>${range.start.format('HH:mm')} a ${endLabel}</strong> (${minutes} min)`;
    })
    .join('<br>');

  return `<p>${intro}<br>${lines}</p>`;
};

export const paginateState: PaginateState = {
  nRecord: 50,
  nRegisters: 0,
  nTotalTareasExtras: 0,
};

export const compareFilters = (a: string, b: string) => {
  return toOrder(a) == toOrder(b)
}

export const isWithinWorkHours = (date: moment.Moment, workHours: any): boolean => {
  if (!workHours?.start || !workHours.end) return false;

  const hour = date.hours();
  const minute = date.minutes();
  const totalMinutes = hour * 60 + minute;

  const workStart = workHours.start.hour * 60 + workHours.start.minute;
  const workEnd = workHours.end.hour * 60 + workHours.end.minute;

  return totalMinutes >= workStart && totalMinutes <= workEnd;
};

export const validateDescription = (description: string, minLength: number = 15): boolean => {
  return typeof description === 'string' && description.trim().length >= minLength;
};

export function hasHoursToRecover(tareoStatus: StatusTask[], currentDate: string): boolean {
  if (!tareoStatus.length) return false;
  const currentStatus = tareoStatus.find(status =>
    status.dDate === moment(currentDate).format('YYYY-MM-DD')
  );
  return currentStatus ? currentStatus.nTotal_Minutos_Recuperar > 0 : false;
}

export const canEditBasedOnStatus = (status: number): boolean => {
  return ![ACTIVITY_STATUS.APROBADO, ACTIVITY_STATUS.APROBADO_CLIENTE].includes(status);
};

export const canDuplicateBasedOnStatus = (status: number): boolean => {
  return ![ACTIVITY_STATUS.APROBADO, ACTIVITY_STATUS.APROBADO_CLIENTE].includes(status);
};

export const isWithinBusinessDayToRegister = (date: moment.Moment): boolean => {
  const today = moment();
  const diffDays = today.diff(date, 'days');
  return diffDays <= 1;
};

export const getAllowedHourTypes = (
  hasHoursToRecover: boolean,
  isWithin24Hours: boolean,
  TIPO_HORA: configurations[]
): configurations[] => {
  if (!isWithin24Hours) {
    return TIPO_HORA.filter(tipo => tipo.sCodigo === '1');
  }
  if (hasHoursToRecover) {
    return TIPO_HORA.filter(tipo =>
      ['1', '4', '5'].includes(tipo.sCodigo)
    );
  } else {
    return TIPO_HORA.filter(tipo =>
      ['1', '2', '3', '4'].includes(tipo.sCodigo)
    );
  }
};

export function getFilteredHoursTypes(
  aAllTareoStatus: AAllTareoStatus[],
  TIPO_HORA: configurations[],
  currentValidations: any,
): configurations[] {
  if (!aAllTareoStatus?.length || !TIPO_HORA?.length || !currentValidations) {
    return TIPO_HORA.filter((tipo: configurations) => tipo.sCodigo === '1');
  }

  const validTareos = aAllTareoStatus.filter(
    (assist: AAllTareoStatus) =>
      ![
        ASSISTANCE_STATUS.FERIADO,
        ASSISTANCE_STATUS.FUERA_DE_HORARIO,
      ].includes(assist.nEstado_Asistencia)
  );

  const lastTwoDays = validTareos
    .slice(-2)
    .map((status: AAllTareoStatus) => status.dDate);

  const midDays = aAllTareoStatus.filter((assist: AAllTareoStatus) =>
    moment(assist.dDate).isBetween(
      moment(lastTwoDays[0]),
      moment(lastTwoDays[1]),
      'day',
      '[]'
    )
  );

  const isIntoMidDays = midDays.some(mid =>
    moment(mid.dDate).isSame(moment(currentValidations.dDate), 'day'))
    &&
    currentValidations.nEntryMark === currentValidations.nReadyMark &&
    currentValidations.nEntryMark !== 0;

  const isWithinLastTwoDays = lastTwoDays.some(date =>
    moment(date).isSame(moment(currentValidations.dDate), 'day')
  );

  let isTodayMarked = moment().isSame(moment(currentValidations.dDate), 'day') && currentValidations.nEntryMark !== 0;

  if (isWithinLastTwoDays || isIntoMidDays || isTodayMarked) {
    return TIPO_HORA;
  }

  return TIPO_HORA.filter((tipo: configurations) => tipo.sCodigo === '1');
}

export function getDateBagde(
  startDate: string | Date,
  EndDate: string | Date,
  showHour: boolean
): DateBagde {
  moment.locale("es");
  let badge = getSimpleBadge("");
  if (startDate && EndDate) {
    return {
      data: {
        dFecha_inicio: moment(startDate).format("DD MMM YYYY"),
        dFecha_Fin: moment(EndDate).format("DD MMM YYYY"),
        dHora_inicio: showHour ? moment(startDate).format("HH:mm") : "",
        dHora_fin: showHour ? moment(EndDate).format("HH:mm") : "",
      },
      css: badge.css
    };
  } else {
    return {
      data: {
        dFecha_inicio: null,
        dFecha_Fin: null,
        dHora_inicio: null,
        dHora_fin: null,
      },
      css: badge.css
    };
  }
}

export function fromTaskDBToTaskList(task: any) {
  let taskList = {
    dFecha_Fin: task.dFecha_Fin,
    dFecha_Registro: task.dFecha_Registro + "T00:00:00",
    dHora_Fin: task.dHora_Fin,
    dHora_Inicio: task.dHora_Inicio,
    nContador_Historico: 0,
    nContador_Obs: 0,
    nEstado: 1,
    sDetalle: task.sDetalle,
    sNombre_Estado: task.sNombre_Estado,
    nMinutos: task.nMinutos,

    sCodigo_Categoria: task.sCodigo_Categoria,
    sCodigo_Proyecto: task.sCodigo_Proyecto,
    sCodigo_Tipo_Actividad: task.sCodigo_Tipo_Actividad,
    sCodigo_Tipo_Horas: task.sCodigo_Tipo_Horas,
    sCodigo_Requerimiento: task.sCodigo_Requerimiento,

    sNombre_Proyecto: task.sNombre_Proyecto,
    sNombre_Tipo_Actividad: task.sNombre_Tipo_Actividad,
    sNombre_Tipo_Horas: task.sNombre_Tipo_Horas,
    sNombre_Categoria: task.sNombre_Categoria,
    sNombre_Requerimiento: task.sNombre_Requerimiento,

    badgeHoraTareo: getSimpleBadge(setTotalHours(Number(task.nMinutos))),
    badgeTipoHoras: getStateBadge(task.sNombre_Tipo_Horas, task.sNombre_Tipo_Horas),
    badgeEstados: getStateBadge(task.sNombre_Estado),
    badgeHFinHini: obteniendoBadgeHfinHini(
      task.dFecha_Fin,
      task.dFecha_Registro,
      task.dHora_Fin,
      task.dHora_Inicio
    ),

    //REPLACE
    nId: 0,
    nId_Cliente: 0,
    nId_Colaborador: 0,
    nId_Falso: 0,
    nId_Usuario: 0,
    sNombre_Cliente: "-",
    sNombre_Colaborador: "-",
    sNombre_Coordinador: "-",
    sTipo_Servicio: "-",

    icEditarTarea: false,
    icEliminarTarea: false,
    icDuplicarTarea: false,
    icObservarTarea: false,
    icAprobarTarea: false,
    icReenviarTarea: false,
    icObservations: false,
    icHistorico: false,

    sColor: "new",
    resalt: "",
    //
  };

  return taskList;
}

export function obteniendoBadgeHfinHini(
  dFecha_Fin: string | null,
  dFecha_Registro: string | null,
  dHora_Fin: string | null,
  dHora_Inicio: string | null
): DateBagde {
  if (dFecha_Fin && dHora_Fin && dHora_Inicio && dFecha_Registro) {
    let [dateIni] = dFecha_Registro.split("T");
    let [dateFin] = dFecha_Fin.split("T");
    let dFecha_inicio = `${moment(dateIni).format("D MMM YYYY")}`;
    let dFecha_Fin_value = `${moment(dateFin).format("D MMM YYYY")}`;

    return {
      data: {
        dFecha_inicio,
        dFecha_Fin: dFecha_Fin_value,
        dHora_inicio: dHora_Inicio ? moment(dHora_Inicio, "HH:mm:ss").format("HH:mm") : null,
        dHora_fin: dHora_Fin ? moment(dHora_Fin, "HH:mm:ss").format("HH:mm") : null,
      },
      css: "text-center font-medium p-1.5 rounded-full text-xs m-auto text-mg-main-blue bg-mg-main-blue-light border-mg-main-blue",
    };
  }

  return {
    data: {
      dFecha_inicio: null,
      dFecha_Fin: null,
      dHora_inicio: null,
      dHora_fin: null,
    },
    css: "",
  };
}

export function getIconDuplicate(bPermiso: boolean, idStatusRequest: number, typeList: string, revisar: number): IconBadge {
  let iconObj: IconBadge = {
    tooltip: 'Duplicar',
    icon: null,
    css: '',
    action: null,
  };
  if (bPermiso && idStatusRequest != ACTIVITY_STATUS.ELIMINADO && typeList == "TASK-LIST-OWN" && revisar == ACTIVITY_REVISION_STATUS.NO_REVISION) {
    let generalCss = 'flex justify-center items-center p-1.5 w-fit rounded-full cursor-pointer ';
    iconObj.icon = {
      name: 'content_copy',
      type: 'material'
    }
    iconObj.css = generalCss + COLORS_BADGE.orange;
  }
  return iconObj;
}

export function convertirCadenaAJSON(cadenaOriginal: string) {
  function cadenaAObjeto(cadena: string) {
    var pares = cadena.split('&');
    var objeto = {};

    pares.forEach(function (par) {
      var partes = par.split('=');
      var clave = partes[0];
      var valor = partes[1] || null;
      // @ts-ignore
      objeto[clave] = valor;
    });

    return objeto;
  }

  var objetoOriginal = cadenaAObjeto(cadenaOriginal);
  var esquemaDeseado = {
    "nId_Usuario": null,
    "numFilter": null,
    "textFilter": null,
    "sTipo_listado": 'TASK-LIST-TEAM',
    "sFilterOne": null,
    "sFilterTwo": null,
    "sFilterThree": null,
    "sFilterFour": null,
    "sFilterFive": null,
    "sFilterSix": null,
    "sFilterSeven": null,
    "sFilterNotification": null,
    "dFecha_Inicio": null,
    "dFecha_Fin": null,
    "startDateTwo": null,
    "endDateTwo": null,
    "dFecha": null,
    "bDownload": false,
    "bListado_individual": false,
    "sFilterOneFormatted": null,
    "sFilterTwoFormatted": null,
    "sFilterThreeFormatted": null,
    "sFilterFourFormatted": null,
    "sFilterFiveFormatted": null,
    "sFilterSixFormatted": null,
    "sFilterSevenFormatted": null,
    "bDownloadAsInt": 0,
    "bListadoIndividualAsInt": 0,
    "numPage": 2,
    "order": "desc",
    "sort": "dFecha_Registro",
    "records": 50
  };

  var objetoFinal = Object.assign({}, esquemaDeseado, objetoOriginal);

  var cadenaFinal = JSON.stringify(objetoFinal);

  return cadenaFinal;
}

export function enabledToApprove(
  idStatusRequest: number,
  idTypeHour: number,
  TaskApprovePermission: boolean,
  typeList: string,
  revisar: number,
  idRevisor: number,
  idAprobador: number[],
  canApproveAllRequirements?: boolean,
): IconBadge {
  let iconObj: IconBadge = {
    tooltip: 'Aprobar',
    icon: null,
    css: '',
    action: null,
  };

  if (
    Number(idTypeHour) == TIME_TYPE.HORARIO_REGULAR &&
    ![ACTIVITY_STATUS.APROBADO, ACTIVITY_STATUS.APROBADO_CLIENTE, ACTIVITY_STATUS.OBSERVADO_CLIENTE, ACTIVITY_STATUS.CORREGIDO, ACTIVITY_STATUS.ELIMINADO].includes(Number(idStatusRequest)) &&
    TaskApprovePermission &&
    (typeList == 'TASK-LIST-TEAM' && Number(revisar) == ACTIVITY_REVISION_STATUS.REVISION && idRevisor == Number(localDB.getItem('idUsuario')))
  ) {
    let generalCss = 'flex justify-center items-center p-1.5 w-fit rounded-full cursor-pointer ';
    iconObj.icon = {
      name: 'check_circle',
      type: 'material'
    }
    iconObj.action = 'approve'
    iconObj.css = generalCss + COLORS_BADGE.green;
  };

  if (
    [TIME_TYPE.COMPENSACIÓN, TIME_TYPE.EXTRAS, TIME_TYPE.RECUPERACIÓN].includes(Number(idTypeHour)) &&
    ![ACTIVITY_STATUS.APROBADO, ACTIVITY_STATUS.APROBADO_CLIENTE, ACTIVITY_STATUS.OBSERVADO_CLIENTE, ACTIVITY_STATUS.CORREGIDO, ACTIVITY_STATUS.ELIMINADO].includes(Number(idStatusRequest)) &&
    TaskApprovePermission &&
    (typeList == 'TASK-LIST-TEAM' && Number(revisar) == ACTIVITY_REVISION_STATUS.REVISION
      && idRevisor == Number(localDB.getItem('idUsuario'))
      && (idAprobador.includes(Number(localDB.getItem('idUsuario'))))
    )
  ) {
    let generalCss = 'flex justify-center items-center p-1.5 w-fit rounded-full cursor-pointer ';
    iconObj.icon = {
      name: 'check_circle',
      type: 'material'
    }
    iconObj.action = 'approve'
    iconObj.css = generalCss + COLORS_BADGE.green;
  };

  if (
    Number(idTypeHour) == TIME_TYPE.HORARIO_REGULAR &&
    ![ACTIVITY_STATUS.APROBADO, ACTIVITY_STATUS.APROBADO_CLIENTE, ACTIVITY_STATUS.OBSERVADO_CLIENTE, ACTIVITY_STATUS.CORREGIDO, ACTIVITY_STATUS.ELIMINADO].includes(Number(idStatusRequest)) &&
    TaskApprovePermission &&
    ((['TASK-LIST-CLIENT-COORDINATOR', 'TASK-LIST-ALL'].includes(typeList) && Number(revisar) == ACTIVITY_REVISION_STATUS.NO_REVISION))
  ) {
    let generalCss = 'flex justify-center items-center p-1.5 w-fit rounded-full cursor-pointer ';
    iconObj.icon = {
      name: 'check_circle',
      type: 'material'
    }
    iconObj.action = 'approve'
    iconObj.css = generalCss + COLORS_BADGE.green;
  };

  if (
    [TIME_TYPE.HORARIO_REGULAR, TIME_TYPE.EXTRAS, TIME_TYPE.COMPENSACIÓN, TIME_TYPE.RECUPERACIÓN].includes(Number(idTypeHour)) &&
    ![ACTIVITY_STATUS.APROBADO, ACTIVITY_STATUS.APROBADO_CLIENTE, ACTIVITY_STATUS.OBSERVADO_CLIENTE, ACTIVITY_STATUS.CORREGIDO, ACTIVITY_STATUS.ELIMINADO].includes(Number(idStatusRequest)) &&
    TaskApprovePermission &&
    ((['TASK-LIST-CLIENT-COORDINATOR', 'TASK-LIST-ALL'].includes(typeList) && Number(revisar) == ACTIVITY_REVISION_STATUS.NO_REVISION))
  ) {
    let generalCss = 'flex justify-center items-center p-1.5 w-fit rounded-full cursor-pointer ';
    iconObj.icon = {
      name: 'check_circle',
      type: 'material'
    }
    iconObj.action = 'approve'
    iconObj.css = generalCss + COLORS_BADGE.green;
  };

  return iconObj;
}

export function enabledToApproveClient(
  TaskApprovePermission: boolean,
  idStatusRequest: number,
  typeList: string,
): IconBadge {
  let iconObj: IconBadge = {
    tooltip: 'Aprobar',
    icon: null,
    css: '',
    action: null,
  };

  if (
    TaskApprovePermission &&
    idStatusRequest != ACTIVITY_STATUS.APROBADO &&
    (['TASK-LIST-CLIENT-COORDINATOR'].includes(typeList))
  ) {
    let generalCss = 'flex justify-center items-center p-1.5 w-fit rounded-full cursor-pointer ';
    iconObj.icon = {
      name: 'check_circle',
      type: 'material'
    }
    iconObj.css = generalCss + COLORS_BADGE.green;
  }
  return iconObj;
}

export function enabledToObserve(
  idUser: any,
  idUserTask: number,
  nCountObs: number,
  TaskObsPermission: boolean,
  idStatusRequest: number,
  typeList: string,
  revisar: number,
  idRevisor?: number
): IconBadge {
  let iconObj: IconBadge = {
    tooltip: 'Observar',
    icon: null,
    css: '',
    action: null,
  };
  idStatusRequest = Number(idStatusRequest)
  let generalCss = 'flex justify-center items-center p-1.5 w-fit rounded-full cursor-pointer ';
  if (typeList == 'TASK-LIST-OWN' && nCountObs) setObserveIcon(iconObj, nCountObs, generalCss);

  const canObserve = idUser !== idUserTask
    ? canObserveOtherTask(TaskObsPermission, idStatusRequest, typeList, revisar, idRevisor)
    : isTeamReviewer(typeList, revisar, idRevisor) && idStatusRequest == ACTIVITY_STATUS.APROBADO;
  if (canObserve) setObserveIcon(iconObj, nCountObs, generalCss);
  return iconObj;
}

function isTeamReviewer(typeList: string, revisar: number, idRevisor?: number): boolean {
  return typeList == 'TASK-LIST-TEAM' && revisar == ACTIVITY_REVISION_STATUS.REVISION && idRevisor == Number(localDB.getItem('idUsuario'));
}

function canObserveOtherTask(
  TaskObsPermission: boolean,
  idStatusRequest: number,
  typeList: string,
  revisar: number,
  idRevisor?: number
): boolean {
  return TaskObsPermission &&
    idStatusRequest != ACTIVITY_STATUS.ELIMINADO &&
    idStatusRequest != ACTIVITY_STATUS.APROBADO_CLIENTE &&
    idStatusRequest != ACTIVITY_STATUS.CORREGIDO &&
    idStatusRequest != ACTIVITY_STATUS.PAGADO &&
    idStatusRequest != ACTIVITY_STATUS.PENDIENTE_PAGO &&
    (isTeamReviewer(typeList, revisar, idRevisor) ||
      (typeList == 'TASK-LIST-ALL' && revisar == ACTIVITY_REVISION_STATUS.NO_REVISION));
}

function setObserveIcon(iconObj: IconBadge, nCountObs: number, generalCss: string): void {
  if (nCountObs) {
    iconObj.icon = {
      name: 'sms_failed',
      type: 'material'
    }
    iconObj.action = 'observe-individual'
    iconObj.css = generalCss + COLORS_BADGE.orange;
  } else {
    iconObj.icon = {
      name: 'add_comment',
      type: 'material'
    }
    iconObj.action = 'observe'
    iconObj.css = generalCss + COLORS_BADGE.teal;
  }
}

export function enabledToObserveClient(
  nCountObs: number,
  TaskObsPermission: boolean,
  typeList: string,
): IconBadge {
  let iconObj: IconBadge = {
    tooltip: 'Observar',
    icon: null,
    css: '',
    action: null,
  };
  let generalCss = 'flex justify-center items-center p-1.5 w-fit rounded-full cursor-pointer ';
  if (
    TaskObsPermission &&
    (['TASK-LIST-CLIENT-COORDINATOR'].includes(typeList))
  ) {
    if (nCountObs) {
      iconObj.icon = {
        name: 'sms_failed',
        type: 'material'
      }
      iconObj.css = generalCss + COLORS_BADGE.orange;
    } else {
      iconObj.icon = {
        name: 'add_comment',
        type: 'material'
      }
      iconObj.css = generalCss + COLORS_BADGE.teal;
    }
  }
  return iconObj;
}

export function enabledToEdit(
  idUser: any,
  idUserTask: number,
  idStatusRequest: number,
  typeList: string,
  TaskEditPermission: boolean,
  revisar: number,
  tipoTarea: number,
  idRevisor?: number
): IconBadge {
  let iconObj: IconBadge = {
    tooltip: 'Editar',
    icon: null,
    css: '',
    action: null,
  };

  if (typeList == "TASK-LIST-ALL" && tipoTarea == TIME_TYPE.HORARIO_REGULAR &&
    ![ACTIVITY_STATUS.APROBADO, ACTIVITY_STATUS.APROBADO_CLIENTE, ACTIVITY_STATUS.CORREGIDO, ACTIVITY_STATUS.ELIMINADO].includes(Number(idStatusRequest))
    && revisar == ACTIVITY_REVISION_STATUS.NO_REVISION) {
    iconObj = getIconEdit(TaskEditPermission);
  }

  if (typeList == 'TASK-LIST-TEAM' && tipoTarea == TIME_TYPE.HORARIO_REGULAR &&
    ![ACTIVITY_STATUS.APROBADO_CLIENTE, ACTIVITY_STATUS.CORREGIDO, ACTIVITY_STATUS.ELIMINADO].includes(Number(idStatusRequest))
    && Number(revisar) == ACTIVITY_REVISION_STATUS.REVISION && idRevisor == Number(localDB.getItem('idUsuario'))) {
    iconObj = getIconEdit(TaskEditPermission);
  }

  if (typeList == "TASK-LIST-OWN" && idUser == idUserTask &&
    ![ACTIVITY_STATUS.APROBADO, ACTIVITY_STATUS.APROBADO_CLIENTE, ACTIVITY_STATUS.CORREGIDO, ACTIVITY_STATUS.PAGADO, ACTIVITY_STATUS.PENDIENTE_PAGO, ACTIVITY_STATUS.ELIMINADO].includes(Number(idStatusRequest))
    && Number(revisar) == ACTIVITY_REVISION_STATUS.NO_REVISION) {
    iconObj = getIconEdit(true);
  }

  if (typeList == "TASK-LIST-OWN" && idUser == idUserTask &&
    tipoTarea == TIME_TYPE.AVANCE && Number(revisar) == ACTIVITY_REVISION_STATUS.NO_REVISION) {
    iconObj = getIconEdit(true);
  }

  return iconObj;
}

export function getIconEdit(bPermiso: boolean): IconBadge {
  // let iconObj: IconBadge = {
  //   tooltip: 'Editar',
  //   icon: null,
  //   css: '',
  //   action: null,
  // };

  let iconObj = getEmptyIcon()
  if (bPermiso) {
    let generalCss = 'flex justify-center items-center p-1.5 w-fit rounded-full cursor-pointer ';
    iconObj.icon = {
      name: 'edit',
      type: 'material'
    }
    iconObj.action = 'update'
    iconObj.css = generalCss + COLORS_BADGE.main;
  }
  return iconObj;
}

export function getIconTrash(bPermiso: boolean): IconBadge {
  let iconObj: IconBadge = {
    tooltip: 'Eliminar',
    icon: null,
    css: '',
    action: null,
  };
  if (bPermiso) {
    let generalCss = 'flex justify-center items-center p-1.5 w-fit rounded-full ';
    iconObj.icon = {
      name: 'delete',
      type: 'material'
    }
    iconObj.css = generalCss + COLORS_BADGE.red;
  }
  return iconObj;
}

export function enabledToDelete(idStatusRequest: number, TaskDeletePermission: boolean, typeList: string, revisar: number): IconBadge {
  let iconObj: IconBadge = {
    tooltip: 'Eliminar',
    icon: null,
    css: '',
    action: null,
  };
  if (
    Number(idStatusRequest) != ACTIVITY_STATUS.APROBADO &&
    Number(idStatusRequest) != ACTIVITY_STATUS.OBSERVADO_CLIENTE &&
    Number(idStatusRequest) != ACTIVITY_STATUS.APROBADO_CLIENTE &&
    Number(idStatusRequest) != ACTIVITY_STATUS.ELIMINADO &&
    Number(idStatusRequest) != ACTIVITY_STATUS.PAGADO &&
    Number(idStatusRequest) != ACTIVITY_STATUS.PENDIENTE_PAGO &&
    TaskDeletePermission && revisar == ACTIVITY_REVISION_STATUS.NO_REVISION && typeList == "TASK-LIST-OWN"
  ) {
    iconObj = getIconTrash(TaskDeletePermission);
  }

  return iconObj;
}

export function showObserver(nCountObs: number, typeList: string, revision: number): IconBadge {
  let iconObj: IconBadge = {
    tooltip: 'Eliminar',
    icon: null,
    css: '',
    action: null,
  };

  if ((typeList == 'TASK-LIST-OWN') && nCountObs) {
    iconObj.tooltip = '';
    iconObj.icon = {
      name: 'sms_failed',
      type: 'material'
    }
    iconObj.css = "flex justify-center items-center p-1.5 w-fit rounded-full text-mg-orange-dark bg-mg-orange-light border-mg-new-orange-dark cursor-pointer"
  }

  if ((typeList == 'TASK-LIST-ALL') && nCountObs) {
    iconObj.tooltip = '';
    iconObj.icon = {
      name: 'sms_failed',
      type: 'material'
    }
    iconObj.css = "flex justify-center items-center p-1.5 w-fit rounded-full text-mg-orange-dark bg-mg-orange-light border-mg-new-orange-dark cursor-pointer"
  }

  if ((typeList == 'TASK-LIST-TEAM') && nCountObs && revision == ACTIVITY_REVISION_STATUS.NO_REVISION) {
    iconObj.tooltip = '';
    iconObj.icon = {
      name: 'sms_failed',
      type: 'material'
    }
    iconObj.css = "flex justify-center items-center p-1.5 w-fit rounded-full text-mg-orange-dark bg-mg-orange-light border-mg-new-orange-dark cursor-pointer"
  }
  return iconObj;
}

/* Tareo modal */
export function updateValidationsHelper(updateValid: UpdateValidation, data: any, executed = false): void {
  if (executed) return;

  const dDate = updateValid.fecha.format("YYYY-MM-DD");
  const tareo = data.aAllTareoStatus.find((x: StatusTask) => x.dDate === dDate);
  if (!tareo) return;

  const nMinutos = updateValid.nMinutos;
  const minutosActuales = tareo.nMaxMinutesRegular ?? 0;
  const nuevoMaximo = Math.max(minutosActuales - nMinutos, 0);

  tareo.nMaxMinutesRegular = nuevoMaximo;
  tareo.nMaxMinutesExtra = nuevoMaximo;
  tareo.nMaxMinutesRegularEditar = nuevoMaximo;
  tareo.nMaxMinutesExtraEditar = nuevoMaximo;

  if (updateValid.task?.sCodigo_Tipo_Horas === "5") {
    tareo.nTotal_Minutos_Recuperar = Math.max(0, (tareo.nTotal_Minutos_Recuperar ?? 0) - nMinutos);
  }

  localDB.setItem('calculatedMaxMinutes', nuevoMaximo.toString());
}

export function getAllowedHourTypesForUpdate(currentHourType: string, allTypes: any[]): any[] {
  switch (currentHourType) {
    case "1": // HORARIO REGULAR
      return allTypes.filter(x => x.sCodigo === "1");
    case "2": // EXTRAS
    case "3": // COMPENSACIÓN
    case "4": // AVANCE
      return allTypes.filter(x => ["1", "2", "3", "4"].includes(x.sCodigo));
    case "5": // RECUPERACIÓN
      return allTypes.filter(x => ["1", "4", "5"].includes(x.sCodigo));
    default:
      return allTypes;
  }
}

export function updateExtrasHelper(
  type: "add" | "diff" | "update_regular" | "update_extra",
  activity: TempTaskModal,
  data: { aAllTareoStatus: StatusTask[] }
): void {
  const date = moment(activity.dFecha_Registro).format("YYYY-MM-DD");
  const tareo = data.aAllTareoStatus.find((x) => x.dDate === date);
  if (!tareo) return;

  switch (type) {
    case "add":
      tareo.nMaxMinutesRegularEditar =
        Number(tareo.nMaxMinutesRegular) + Number(activity.nMinutos);
      tareo.nMaxMinutesExtraEditar =
        Number(tareo.nMaxMinutesExtra) + Number(activity.nMinutos);
      break;

    case "diff":
      tareo.nMaxMinutesRegularEditar = Number(tareo.nMaxMinutesRegular);
      tareo.nMaxMinutesExtraEditar = Number(tareo.nMaxMinutesExtra);
      break;

    case "update_regular":
      tareo.nMaxMinutesExtraEditar -= Number(activity.nMinutos);
      break;

    case "update_extra":
      tareo.nMaxMinutesRegularEditar -= Number(activity.nMinutos);
      break;
  }
}

export function updateTaskStatusByDateHelper(
  fecha: moment.Moment,
  minutos: number,
  data: { aAllTareoStatus: StatusTask[]; oTareoByStatus: Record<string, string[]> }
): void {
  const dDate = fecha.format("YYYY-MM-DD");
  let sEstado = "FALTA";
  const tareo = data.aAllTareoStatus.find((x) => x.dDate === dDate);

  if (!tareo) return;

  tareo.nReady = Number(tareo.nReady) - minutos;
  const totalALlenar = tareo.nRequired;
  const llenadoHastaAhora = tareo.nReady;

  switch (true) {
    case totalALlenar - llenadoHastaAhora > 0:
      sEstado = "INCOMPLETO";
      break;

    case totalALlenar - llenadoHastaAhora <= 0:
      sEstado = "COMPLETO";
      break;

    case totalALlenar - llenadoHastaAhora === totalALlenar:
      sEstado = "FALTA";
      break;
  }
  tareo.sEstado = sEstado;
  tareo.nMaxMinutesRegular = Number(tareo.nRequired) - Number(tareo.nReady);
  const oTareoByStatus = groupBy(data.aAllTareoStatus, (i) => i.sEstado);
  const mappedByStatus: Record<string, string[]> = {};

  for (const key in oTareoByStatus) {
    mappedByStatus[key] = oTareoByStatus[key].map((value: StatusTask) => value.dDate);
  }

  setTimeout(() => {
    data.oTareoByStatus["FALTA"] = mappedByStatus["FALTA"] ?? [];
    data.oTareoByStatus["COMPLETO"] = mappedByStatus["COMPLETO"] ?? [];
    data.oTareoByStatus["INCOMPLETO"] = mappedByStatus["INCOMPLETO"] ?? [];
  }, 0);
}

export function mapErrorsToTaskArrays<T>(
  resp: GenericResponse<any>
): DataErrorResult<T> {
  let bdArray: T[] = [];
  let listArray: T[] = [];

  if (resp.data?.errors?.length >= 1) {
    let uuid = 0;
    resp.data.errors.id = uuid++;

    resp.data.errors.forEach((x: any) => {
      x.dFecha_Registro = moment(x.dFecha_Registro).toDate();
      bdArray.unshift(x);
    });
  }
  return { bdArray, listArray };
}

/* Footer */
export function mapInputsToSend(array: any[], object: any) {
  const propiedades = Object.entries(object);
  const startIndex = 4;
  for (let i = startIndex; i < propiedades.length; i++) {
    const [propiedad, valor] = propiedades[i];
    let a = array.find((obj) => obj.prop == propiedad)
    if (a) {
      object[propiedad] = a.value ? ((a.value == 'true' || a.value == 'false') ? JSON.parse(a.value) : a.value) : null;
    } else {
      if (propiedad == 'bListado_individual' || propiedad == 'bDownload') {
        object[propiedad] = false;
      } else {
        object[propiedad] = null;
      }
    }
  }
  return object
}

export function getInputsPart2(string: string) {
  let array: any[] = [];
  let x = string.split('&');
  x.forEach((elem) => {
    if (elem) {
      let obj = {
        prop: elem.split('=')[0],
        value: elem.split('=')[1] === 'null' ? null : decodeURIComponent(elem.split('=')[1]),
      };
      if (obj.value !== null) {
        array.push(obj);
      }
    }
  });
  return array;
}

export function getBadgeForTaskStateWithTooltip(
  sEstado: string,
  sNombre_Estado: string,
  tooltip: string
): BadgeStatus {
  let color = '';
  switch (sNombre_Estado.toUpperCase()) {
    case 'PENDIENTE':
      color = 'orange';
      break;
    case 'APROBADO':
      color = 'green';
      break;
    case 'RECHAZADO':
      color = 'red';
      break;
    case 'OBSERVADO':
      color = 'orange';
      break;
    default:
      color = 'gray';
      break;
  }
  return getSimpleBadge(sNombre_Estado, tooltip, color, 'border !max-w-[12rem]');
}

export function getBadgeForRequestTypeWithTooltip(
  nTipoTiempo: string,
  label: string,
  tooltip: string
): BadgeStatus {
  let color = '';
  switch (nTipoTiempo) {
    case "1":
      color = 'purple';
      break;
    case "2":
      color = 'green';
      break;
    case "3":
      color = 'yellow';
      break;
    case "4":
      color = 'pink';
      break;
    case "5":
      color = 'teal';
      break;
  }
  return getSimpleBadge(label, tooltip, color, 'border !max-w-[12rem]');
}

/* Se usa para el mapeo de la tabla */
export function getBadgeForTaskState(
  sEstado: string,
  sNombre_Estado: string
): BadgeStatus {
  let color = '';
  //TODO Cambiar por enum de estados de tareo
  switch (sNombre_Estado.toUpperCase()) {
    case 'PENDIENTE':
      color = 'orange';
      break;
    case 'APROBADO':
      color = 'green';
      break;
    case 'RECHAZADO':
      color = 'red';
      break;
    case 'OBSERVADO':
      color = 'orange';
      break;
    default:
      color = 'gray';
      break;
  }
  return getSimpleBadge(sNombre_Estado, sNombre_Estado, color, 'border !max-w-[12rem]');
}

export function getBadgeForRequestType(
  nTipoTiempo: string,
  label: string
): BadgeStatus {
  let color = '';
  //TODO Cambiar tipo de horas por enums para no usar directamente el texto (TIME_TYPE)
  switch (nTipoTiempo) {
    case "1":
      color = 'purple';
      break;
    case "2":
      color = 'green';
      break;
    case "3":
      color = 'yellow';
      break;
    case "4":
      color = 'pink';
      break;
    case "5":
      color = 'teal';
      break;
  }
  return getSimpleBadge(label, label, color, 'border !max-w-[12rem]');
}

export function responseAPI(response: any, _smartAlertService: SmartAlertService, component?: ComponentInitConfigTask) {
  if (response.isSuccess) {
    _smartAlertService.init({
      type: "success",
      showCancelButton: false,
      timer: 1.5,
      timerProgressBar: true,
    })
  } else {
    _smartAlertService.init({
      type: "warning",
      showCancelButton: false,
      message: response.message,
    })
  }
  // component.refresh = true;
}